'use client';

import { AccountInfo, ICustomerPointTaskItem, IPointsMallInfo } from '@/api/types/account';
import { CrmPointRule } from '@/api/types/public';
import UnauthenticatedBanner from '@/app/[variants]/account/redeemable-items/components/UnauthenticatedBanner';
import Breadcrumbs from '@/components/Breadcrumbs';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useReporting } from '@/hooks/useReporting';
import { ProviderType } from '@/reporting';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box } from '@mui/material';
import { QuestionFilled, QuestionOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import PointsTask from './PointsTask';
import PromoBanner from './PromoBanner';
/**
 * 页面标题和帮助信息组件
 */
export const PageHeader = ({
  onTipsClick,
  deviceType,
}: {
  onTipsClick: () => void;
  deviceType: EPlatform;
}) => {
  const t = useTranslations('account.pointsMall.uiComponents');
  const router = useRouter();
  const { trackEvent } = useReporting();
  const PointsMallPageBtn_Click = (btnName: string) => {
    trackEvent(
      {
        eventName: 'PointsMallPageBtn_Click',
        properties: {
          btn_name: btnName,
        },
      },
      [ProviderType.SENSORS],
    );
  };
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: deviceType === EPlatform.WEB ? 4 : 2,
        pt: deviceType === EPlatform.WEB ? 0 : '10px',
      }}
    >
      <WebOnly>
        <h1 className="flex items-center">
          <span className="font-bb text-24">{t('pageTitle')}</span>
          <span className="text-14 ml-12">{t('redeemForFreeShipping')}</span>
        </h1>
      </WebOnly>
      <WapOnly>
        <Breadcrumbs
          items={[
            {
              label: t('home'),
              href: '/',
              onClick: () => {
                router.push('/');
              },
            },
            {
              label: t('pageTitle'),
            },
          ]}
        />
      </WapOnly>

      <Box
        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        onClick={() => {
          onTipsClick();
          PointsMallPageBtn_Click('Learn about ZEELOOL points');
        }}
      >
        <WebOnly>
          <span className="text-12 underline mr-4">{t('learnAboutZeeloolPoints')}</span>
          <QuestionFilled className="text-16 text-[#b1b1b1]" />
        </WebOnly>
      </Box>
    </Box>
  );
};

/**
 * 已认证用户横幅组件
 */
export const AuthenticatedBanner = ({
  currentUserInfo,
  currentFreeData,
  deviceType,
  crmPointsRules,
  customerPointTask,
  onTipsClick,
  refreshFreeData,
}: {
  currentUserInfo: AccountInfo;
  currentFreeData: IPointsMallInfo;
  deviceType: EPlatform;
  crmPointsRules: CrmPointRule;
  customerPointTask: ICustomerPointTaskItem[];
  onTipsClick: () => void;
  refreshFreeData: () => void;
}) => {
  const t = useTranslations('account.pointsMall.uiComponents');
  return currentUserInfo?.is_member === 1 && currentFreeData ? (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: deviceType === EPlatform.WEB ? { xs: 'column', lg: 'row' } : 'column',
        mb: deviceType === EPlatform.WEB ? 5 : 4.5,
        gap: deviceType === EPlatform.WEB ? 0 : 1,
      }}
    >
      {/* 促销横幅 - 左侧 */}
      <Box
        sx={{
          width: deviceType === EPlatform.WEB ? { xs: '100%', lg: '30%' } : '100%',
          mb: deviceType === EPlatform.WEB ? { xs: 2, lg: 0 } : 0,
        }}
      >
        <PromoBanner
          freeData={currentFreeData}
          crmPointsRules={crmPointsRules}
          onTipsClick={onTipsClick}
        />
      </Box>
      {/* 兑换步骤 - 右侧 */}
      <Box sx={{ width: deviceType === EPlatform.WEB ? { xs: '100%', lg: '68.6%' } : '100%' }}>
        <PointsTask customerPointTask={customerPointTask} deviceType={deviceType} />
      </Box>
    </Box>
  ) : (
    <UnauthenticatedBanner btnText={t('joinForFree')} refreshFreeData={refreshFreeData} />
  );
};

/**
 * 未登录用户横幅组件
 */
export const UnauthenticatedBannerWrapper = ({
  refreshFreeData,
}: {
  refreshFreeData: () => void;
}) => {
  const t = useTranslations('account.pointsMall.uiComponents');
  return <UnauthenticatedBanner btnText={t('signIn')} refreshFreeData={refreshFreeData} />;
};

/**
 * 页面内容容器组件
 * 包含标题和横幅部分的通用布局
 */
export const PageContent = ({
  children,
  isLoggedIn,
  currentUserInfo,
  currentFreeData,
  onTipsClick,
  deviceType,
  crmPointsRules,
  customerPointTask,
  refreshFreeData,
}: {
  children: React.ReactNode;
  isLoggedIn: boolean;
  currentUserInfo: AccountInfo;
  currentFreeData: IPointsMallInfo;
  onTipsClick: () => void;
  deviceType: EPlatform;
  crmPointsRules: CrmPointRule;
  customerPointTask: ICustomerPointTaskItem[];
  refreshFreeData: () => void;
}) => {
  const t = useTranslations('account.pointsMall.uiComponents');
  const { trackEvent } = useReporting();
  const PointsMallPageBtn_Click = (btnName: string) => {
    trackEvent(
      {
        eventName: 'PointsMallPageBtn_Click',
        properties: {
          btn_name: btnName,
        },
      },
      [ProviderType.SENSORS],
    );
  };
  return (
    <div className="RedeemableItemsPage mx-auto">
      <WapOnly>
        <div className="h-32px w-full bg-auxiliary-light-purple text-[13px] px-14 py-8 sticky top-[43px] z-10 flex items-center justify-between">
          <span>{t('redeemForFreeShipping')}</span>
          {currentUserInfo?.is_member !== 1 && (
            <QuestionOutlined
              className="text-14"
              onClick={() => {
                onTipsClick();
                PointsMallPageBtn_Click('Learn about ZEELOOL points');
              }}
            />
          )}
        </div>
      </WapOnly>
      <div className={`${deviceType === EPlatform.WEB ? 'w-full' : 'w-[calc(100%-28px)] mx-auto'}`}>
        {/* 主内容区 */}
        <Box>
          {/* 标题和帮助信息 */}
          <PageHeader onTipsClick={onTipsClick} deviceType={deviceType} />

          {/* 根据登录状态显示不同横幅 */}
          {isLoggedIn ? (
            <AuthenticatedBanner
              currentUserInfo={currentUserInfo}
              currentFreeData={currentFreeData}
              deviceType={deviceType}
              crmPointsRules={crmPointsRules}
              customerPointTask={customerPointTask}
              onTipsClick={onTipsClick}
              refreshFreeData={refreshFreeData}
            />
          ) : (
            <UnauthenticatedBannerWrapper refreshFreeData={refreshFreeData} />
          )}
        </Box>
      </div>

      {/* 子组件内容 */}
      {children}
    </div>
  );
};
